import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';

import { useReloadHybridSearchAfterMapInteraction } from './useReloadHybridSearchAfterMapInteraction';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import { setAwaitingGeoFocus } from '../../../actions/geoFocus';
import { useMapController } from '../../../controller/useMapController';
import { selectIsAwaitingGeoFocus } from '../../../selectors';

/**
 * Фаза 2 смены гео в фильтрах: после программного фокуса карты на новую область
 * (setBounds в useBoundsSync по bbox из applyGeoChange/get-bbox либо из фолбэк-ответа /map)
 * грузим пины/листинг под ФАКТИЧЕСКИЙ зум - тем же reload-сценарием, что жесты/кнопки/клик
 * по кластеру. Целевой зум появляется только после фокуса (его подбирает карта под размер
 * контейнера), поэтому до этого момента запрос за данными ушёл бы «под старый зум»:
 * features легли бы не под тот precision, а бэк посчитал бы выдачу по старым center/zoom
 * из queryString.
 *
 * Снимок берём из controller.getSnapshot() (visibleBounds с учётом desktop listing), а не
 * из redux mapBounds - он обновляется отложенно (changeBounds через setTimeout+flushSync)
 * и на момент вызова ещё содержит старый вид. fetchMapData по mapSnapshot пересчитает и
 * отправляемый queryString (center/zoom) под этот снимок.
 *
 * Срабатывает только когда фокус вызван гео-сменой в фильтрах - флаг isAwaitingGeoFocus
 * взводит applyGeoChange. Полигон/регион/жесты флаг не трогают.
 * Ср. isAwaitingGeoUpdate (FiltersContainer) - ref для updateBounds первой фазы
 * legacy/мобильного пути, вторую фазу он не запускает.
 */
export function useGeoFocusRefetch(): () => void {
  const dispatch = useDispatch<TThunkDispatch>();
  const store = useStore<IApplicationState>();
  const controller = useMapController();
  const reload = useReloadHybridSearchAfterMapInteraction();

  return React.useCallback(() => {
    if (!selectIsAwaitingGeoFocus(store.getState())) {
      return;
    }

    // Сброс до reload: исключает повторный вход в рамках одного фокуса.
    dispatch(setAwaitingGeoFocus(false));
    reload({ mapSnapshot: controller.getSnapshot() });
  }, [dispatch, store, controller, reload]);
}
